import { useRef, useState } from "react";
import { Paperclip, Pause, Play, RotateCcw } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { formatBytes, formatDuration, mediaUrl } from "../lib/media";
import { dayjs } from "../lib/time";
import "../player-chat.css";

export function compactMessageTimestamp(value, timezone, lang, nowValue = null) {
  if (!value) return "";
  const local = dayjs.utc(value).tz(timezone || "UTC").locale(lang || "en");
  const now = (nowValue ? dayjs.utc(nowValue) : dayjs.utc()).tz(timezone || "UTC");
  return local.isSame(now, "day") ? local.format("HH:mm") : local.format("DD MMM, HH:mm");
}

export function CompactVoiceControl({ audio }) {
  const { t } = useI18n();
  const elementRef = useRef(null);
  const fallbackDuration = Math.max(0, Number(audio?.duration) || 0);
  const [duration, setDuration] = useState(fallbackDuration);
  const [currentTime, setCurrentTime] = useState(0);
  const [state, setState] = useState("idle");

  const togglePlayback = async () => {
    const element = elementRef.current;
    if (!element) return;
    if (!element.paused) {
      element.pause();
      setState("paused");
      return;
    }
    setState("loading");
    try {
      await element.play();
      setState("playing");
    } catch {
      setState("error");
    }
  };

  const retry = () => {
    const element = elementRef.current;
    if (!element) return;
    setCurrentTime(0);
    setState("idle");
    element.load();
  };

  const seek = (event) => {
    const element = elementRef.current;
    if (!element) return;
    const next = Number(event.target.value) || 0;
    element.currentTime = next;
    setCurrentTime(next);
  };

  const playableDuration = Number.isFinite(duration) && duration > 0 ? duration : fallbackDuration;
  const playing = state === "playing" || state === "loading";
  return <div className="min-w-0" data-testid="compact-player-voice">
    <audio
      ref={elementRef}
      preload="metadata"
      playsInline
      src={mediaUrl(audio.download_path)}
      className="absolute w-px h-px overflow-hidden opacity-0 pointer-events-none"
      onLoadedMetadata={(event) => {
        const next = Number(event.currentTarget.duration);
        if (Number.isFinite(next) && next > 0) setDuration(next);
      }}
      onDurationChange={(event) => {
        const next = Number(event.currentTarget.duration);
        if (Number.isFinite(next) && next > 0) setDuration(next);
      }}
      onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
      onPlaying={() => setState("playing")}
      onPause={() => setState((current) => current === "ended" ? current : "paused")}
      onWaiting={() => setState("loading")}
      onEnded={() => { setState("ended"); setCurrentTime(playableDuration); }}
      onError={() => setState("error")}
      data-testid="player-request-audio"
    />
    <div className="flex items-center gap-2 min-w-0">
      <button
        type="button"
        onClick={togglePlayback}
        className="w-8 h-8 shrink-0 rounded-full grid place-items-center"
        style={{ background: "var(--pc-cyan)", color: "#001014" }}
        aria-label={playing ? t("pauseAudio") : t("playAudio")}
        data-testid="player-voice-toggle"
      >
        {playing ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
      </button>
      <div className="min-w-0 flex-1 flex items-center gap-2">
        <input
          type="range"
          min="0"
          max={playableDuration || 1}
          step="0.05"
          value={Math.min(currentTime, playableDuration || 1)}
          onChange={seek}
          className="block min-w-0 flex-1 h-1.5 cursor-pointer"
          style={{ accentColor: "var(--pc-cyan)" }}
          aria-label={t("audioProgress")}
          data-testid="player-voice-progress"
        />
        <span className="shrink-0 text-[10px] tabular-nums" style={{ color: "var(--pc-muted)" }} data-testid="player-voice-duration">
          {state === "playing" || state === "paused" || state === "loading"
            ? `${formatDuration(currentTime)} / ${formatDuration(playableDuration)}`
            : formatDuration(playableDuration)}
        </span>
      </div>
    </div>
    {state === "error" && <div className="flex items-center justify-between gap-2 mt-2 text-[11px]" style={{ color: "#ff9c9c" }} data-testid="player-voice-error">
      <span>{t("audioCouldNotLoad")}</span>
      <button type="button" onClick={retry} className="inline-flex items-center gap-1" style={{ color: "var(--pc-cyan)" }}>
        <RotateCcw size={12} /> {t("retry")}
      </button>
    </div>}
  </div>;
}

export function joinsPreviousMessage(message, previous) {
  if (!previous || message.type === "system" || previous.type === "system") return false;
  const gap = Date.parse(message.created_at) - Date.parse(previous.created_at);
  return message.sender_id === previous.sender_id
    && message.sender_role === previous.sender_role
    && Number.isFinite(gap) && gap >= 0 && gap <= 5 * 60 * 1000;
}

export default function PlayerRequestMessage({ message, timezone, previousMessage }) {
  const { t, lang } = useI18n();
  if (message.type === "system") {
    return <div className="pc-request-system-message text-center text-xs py-2" style={{ color: "var(--pc-muted)" }} data-testid="request-system-message">{message.text}</div>;
  }
  const own = message.sender_role === "player";
  const voice = Boolean(message.audio);
  const textOnly = Boolean(message.text) && !voice && !message.attachment;
  const timestamp = compactMessageTimestamp(message.created_at, timezone, lang);
  const seen = own && Boolean(message.seen_by_care_at);
  return <div className={`pc-request-message-row flex ${own ? "justify-end" : "justify-start"}`}
    data-grouped={joinsPreviousMessage(message, previousMessage) || undefined}
    data-testid={`player-message-${own ? "own" : "care"}`}>
    <article
      className={`pc-request-bubble min-w-0 ${voice ? "pc-request-bubble--voice" : ""}`}
      data-inline-time={textOnly || undefined}
      style={{
        background: own ? "var(--pc-cyan-dim)" : "var(--pc-charcoal-2)",
        border: "1px solid var(--pc-border)",
        borderBottomRightRadius: own ? 5 : undefined,
        borderBottomLeftRadius: own ? undefined : 5,
      }}
    >
      {message.text && <p className="pc-request-bubble__text"><span data-testid="message-text">{message.text}</span>{textOnly && <span
        className="pc-request-bubble__time-space" aria-hidden="true"
      >{timestamp}{seen && <span className="pc-request-bubble__seen">✓✓</span>}</span>}</p>}
      {message.audio && <CompactVoiceControl audio={message.audio} />}
      {message.attachment && <a
        className="flex items-center gap-2 text-sm min-w-0"
        style={{ color: "var(--pc-cyan)" }}
        href={mediaUrl(message.attachment.download_path)}
        target="_blank"
        rel="noreferrer"
        data-testid="player-request-attachment"
      >
        <Paperclip size={15} className="shrink-0" />
        <span className="truncate">{message.attachment.original_filename}</span>
        <span className="shrink-0" style={{ color: "var(--pc-muted)" }}>({formatBytes(message.attachment.file_size)})</span>
      </a>}
      <div className="pc-request-bubble__meta" data-testid="message-meta">
        <time dateTime={message.created_at}>{timestamp}</time>
        {seen && <span className="pc-request-bubble__seen" title={t("seen")} aria-label={t("seen")} data-testid="care-seen">✓✓</span>}
      </div>
    </article>
  </div>;
}
